import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { openUsercentricsSecondLayer } from '@xing-com/cookie-consent-usercentrics';
import type { UsercentricsObject } from '@xing-com/cookie-consent-usercentrics';
import { PAY_OR_CONSENT_UPSELL_HREF } from '@xing-com/crate-upsell-utils';
import { useLoginAppUrl } from '@xing-com/crate-xinglet';
import { Divider } from '@xing-com/divider';
import { useExperiment } from '@xing-com/hub';
import { IconArrowDown, IconArrowUp } from '@xing-com/icons';
import { PopUpWindow } from '@xing-com/pop-up-window';
import { BodyCopy } from '@xing-com/typography';
import { XingLogo } from '@xing-com/xing-logo';

import * as Styled from './pay-or-consent.styled';
import { usePayOrConsentTracking } from './use-pay-or-consent-tracking';

const USERCENTRICS_CLOSING_EVENTS = new Set([
  'ACCEPT_ALL',
  'DENY_ALL',
  'SAVE',
  'CMP_CLOSED',
  'CMP_CLOSE',
  'CLOSE',
  'CLOSED',
]);

const getUsercentricsObject = (): UsercentricsObject | undefined => {
  return window.__ucCmp as UsercentricsObject | undefined;
};

export const PayOrConsent: React.FC = () => {
  const isPubliftExperiment = useExperiment('ABACUS-555') === 'B';
  const settingsId = isPubliftExperiment ? 'bKHOPD_JVewXgX' : undefined;

  const [accordionOpen, setAccordionOpen] = useState(false);
  const [isUsercentricsSecondLayerOpen, setIsUsercentricsSecondLayerOpen] =
    useState(false);
  const loginAppUrl = useLoginAppUrl();
  const {
    getTrackedLoginUrl,
    trackConsentAccept,
    trackGetPremium,
    trackInformationExpand,
    trackLogIn,
    trackXingPrivacy,
  } = usePayOrConsentTracking();

  const handleOpenPartners = (
    event: React.MouseEvent<HTMLAnchorElement>
  ): void => {
    event.preventDefault();

    void openUsercentricsSecondLayer(settingsId)
      .then(() => {
        setIsUsercentricsSecondLayerOpen(true);
      })
      .catch(() => {
        setIsUsercentricsSecondLayerOpen(false);
      });
  };

  const handleAcceptAll = (
    event: React.MouseEvent<HTMLAnchorElement>
  ): void => {
    event.preventDefault();
    trackConsentAccept();

    getUsercentricsObject()
      ?.acceptAllConsents?.()
      .catch(() => {
        // If Usercentrics is blocked, consent remains false and the wall stays visible.
      });
  };

  const handlePayClick = (): void => {
    trackGetPremium();
  };

  const handleLoginClick = (): void => {
    trackLogIn();
  };

  const handleToggleAccordion = (): void => {
    if (!accordionOpen) {
      trackInformationExpand();
    }

    setAccordionOpen(!accordionOpen);
  };

  const handlePrivacyClick = (): void => {
    trackXingPrivacy();
  };

  const loginUrl = getTrackedLoginUrl(loginAppUrl);

  useEffect(() => {
    if (!isUsercentricsSecondLayerOpen) {
      return;
    }

    const handleCmpEvent = (event: Event): void => {
      const cmpEvent = event as CustomEvent<{ type?: string }>;

      if (
        cmpEvent.detail?.type &&
        USERCENTRICS_CLOSING_EVENTS.has(cmpEvent.detail.type)
      ) {
        setIsUsercentricsSecondLayerOpen(false);
      }
    };

    window.addEventListener('UC_UI_CMP_EVENT', handleCmpEvent);

    return () => {
      window.removeEventListener('UC_UI_CMP_EVENT', handleCmpEvent);
    };
  }, [isUsercentricsSecondLayerOpen]);

  if (isUsercentricsSecondLayerOpen) {
    return null;
  }

  return (
    <PopUpWindow isShown={true}>
      <Styled.DialogContent>
        <Styled.Header>
          <XingLogo size={106} variant="duo" />
        </Styled.Header>
        <Styled.TopArea>
          <Styled.TopAreaHeader size="small">
            <FormattedMessage id="PAY_OR_CONSENT_TITLE" />
          </Styled.TopAreaHeader>
          <Styled.TopAreaBody size="medium">
            <FormattedMessage id="PAY_OR_CONSENT_DESCRIPTION" />
          </Styled.TopAreaBody>
          <Styled.PremiumButtonWrapper>
            <Styled.PremiumButton
              href={PAY_OR_CONSENT_UPSELL_HREF}
              onClick={handlePayClick}
              variant="primary"
              size="medium"
            >
              <FormattedMessage id="PAY_OR_CONSENT_PREMIUM_CTA" />
            </Styled.PremiumButton>
          </Styled.PremiumButtonWrapper>
          <BodyCopy size="small" noMargin>
            <FormattedMessage
              id="PAY_OR_CONSENT_LOGIN"
              values={{
                br: () => <br />,
                a: (chunks) => (
                  <a href={loginUrl} onClick={handleLoginClick}>
                    {chunks}
                  </a>
                ),
              }}
            />
          </BodyCopy>
        </Styled.TopArea>
        <Divider />
        <Styled.BottomArea>
          <BodyCopy size="small">
            <FormattedMessage
              id="PAY_OR_CONSENT_BOTTOM_MAIN"
              values={{
                CMP: (chunks) => (
                  <Styled.Link href="#" onClick={handleOpenPartners}>
                    {chunks}
                  </Styled.Link>
                ),
              }}
            />
          </BodyCopy>
          <TextButton
            size="small"
            onClick={handleToggleAccordion}
            icon={accordionOpen ? IconArrowUp : IconArrowDown}
            iconAfter={true}
          >
            <FormattedMessage id="PAY_OR_CONSENT_BOTTOM_COLLAPSABLE_TITLE" />
          </TextButton>
          <Styled.BottomAreaContent $open={accordionOpen}>
            <FormattedMessage
              id="PAY_OR_CONSENT_BOTTOM_COLLAPSABLE_CONTENT"
              values={{
                P: (chunks) => (
                  <Styled.Paragraph size="small">{chunks}</Styled.Paragraph>
                ),
                Title: (chunks) => <Styled.Title>{chunks}</Styled.Title>,
                CMP: (chunks) => (
                  <Styled.Link href="#" onClick={handleOpenPartners}>
                    {chunks}
                  </Styled.Link>
                ),
                Accept: (chunks) => (
                  <Styled.Link href="#" onClick={handleAcceptAll}>
                    {chunks}
                  </Styled.Link>
                ),
                Divider: () => <Styled.Divider aria-hidden="true" />,
              }}
            />
          </Styled.BottomAreaContent>
        </Styled.BottomArea>
        <Styled.Footer>
          <TextButton
            size="small"
            fontWeight="regular"
            href="https://www.xing.com/legalnotice"
          >
            <FormattedMessage id="PAY_OR_CONSENT_FOOTER_IMPRESSUM" />
          </TextButton>
          <TextButton
            size="small"
            fontWeight="regular"
            href="https://privacy.xing.com/datenschutzerklaerung"
            onClick={handlePrivacyClick}
          >
            <FormattedMessage id="PAY_OR_CONSENT_FOOTER_PRIVACY" />
          </TextButton>
          <TextButton
            size="small"
            fontWeight="regular"
            href="https://www.xing.com/terms"
          >
            <FormattedMessage id="PAY_OR_CONSENT_FOOTER_TERMS" />
          </TextButton>
        </Styled.Footer>
      </Styled.DialogContent>
    </PopUpWindow>
  );
};
